import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AcceptInvite - Handles one-click invite acceptance
 * 
 * When a user clicks an invite link (/invite/:token), this component:
 * 1. Validates the invite token with the backend
 * 2. Auto-creates a guest user account (or links to existing)
 * 3. Stores auth session in localStorage
 * 4. Redirects to the target project
 */
export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState('');
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    if (token) {
      acceptInvite(token);
    }
  }, [token]);

  const acceptInvite = async (inviteToken) => {
    try {
      setStatus('processing');
      
      const response = await fetch(`${BACKEND_URL}/api/invites/accept/${inviteToken}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to accept invite');
      }
      
      // Store user session in localStorage (same format as Login component)
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('sketchHubUser', JSON.stringify(data.user));
        localStorage.setItem('authToken', data.token);
      }
      
      setProjectName(data.project_name);
      setStatus('success');
      
      // Show success toast
      toast.success(`Welcome! You're now part of "${data.project_name}"`);
      
      // Redirect to project after brief delay
      setTimeout(() => {
        navigate(data.redirect_url || `/sketch?project=${data.project_id}`);
      }, 1500);
      
    } catch (err) {
      console.error('Invite acceptance error:', err);
      setError(err.message);
      setStatus('error');
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/5 border-white/10 backdrop-blur-md" data-testid="accept-invite-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-500/20 flex items-center justify-center">
            {status === 'processing' && (
              <svg className="w-8 h-8 text-amber-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {status === 'success' && (
              <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {status === 'error' && (
              <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <CardTitle className="text-2xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            {status === 'processing' && 'Accepting Invite...'}
            {status === 'success' && 'Welcome!'}
            {status === 'error' && 'Invite Error'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'processing' && (
            <p className="text-white/60" data-testid="invite-processing">
              Setting up your access to the project...
            </p>
          )}
          
          {status === 'success' && (
            <div className="space-y-4">
              <p className="text-white/80" data-testid="invite-success">
                You've been added to <span className="text-amber-400 font-semibold">"{projectName}"</span>
              </p>
              <p className="text-white/50 text-sm">
                Redirecting you to the project...
              </p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="space-y-4">
              <p className="text-red-400" data-testid="invite-error">
                {error}
              </p>
              <div className="flex flex-col gap-2">
                <Button 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={() => navigate('/sketch')}
                  data-testid="go-to-sketch-hub"
                >
                  Go to Sketch Hub
                </Button>
                <Button 
                  variant="ghost"
                  className="text-white/60 hover:text-white"
                  onClick={() => navigate('/')}
                  data-testid="go-home"
                >
                  Go Home
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
